<script lang="ts">
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import box from "@core/assets/icons/box.svg?raw";
	import leaf from "@core/assets/icons/leaf.svg?raw";
	import shield from "@core/assets/icons/shield.svg?raw";
	import { Country } from "@core/schema/Country.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import { Locale } from "@core/schema/Locale.js";
	import { SubscriptionFrequency } from "@core/schema/SubscriptionFrequency.js";
	import { SubscriptionSelection } from "@core/schema/SubscriptionSelection.js";
	import { SubscriptionSize } from "@core/schema/SubscriptionSize.js";
	import { SubscriptionVariant } from "@core/schema/SubscriptionVariant.js";
	import { subscriptionSizeNameMap } from "@core/schema/names/subscriptionSizeNameMap.js";
	import { subscriptionVariantNameMap } from "@core/schema/names/subscriptionVariantNameMap.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { calculateMembershipShippingDate } from "@core/schema/utils/calculateMembershipShippingDate.js";
	import { calculateSubscriptionSavings } from "@core/schema/utils/calculateSubscriptionSavings.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { subscriptionFrequencyToWeeksMap } from "@core/schema/utils/subscriptionFrequencyToWeeksMap.js";
	import { subscriptionVariantIconMap } from "@core/schema/utils/subscriptionVariantIconMap.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { formatPercentage } from "@core/utils/formatPercentage.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { makeInflect } from "@core/utils/makeInflect.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { CartStep } from "../cart/CartStep.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import EcoFriendlyTip from "./EcoFriendlyTip.svelte";
	import FathersChoiceB2cOnly from "./FathersChoiceB2cOnly.svelte";
	import FathersChoiceCancellable from "./FathersChoiceCancellable.translations.svelte";
	import ProductCardMedia from "./ProductCardMedia.svelte";
	import Stepper from "./Stepper.svelte";
	import SubcategoryButton from "./SubcategoryButton.svelte";
	import SubscriptionDiscountChip from "./SubscriptionDiscountChip.svelte";
	import SubscriptionSelectionCard from "./SubscriptionSelectionCard.svelte";
	import translations from "./SubscriptionSideModal.translations.json" with { type: "json" };
	import Button from "./common/Button.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import { ButtonVariant } from "./common/ButtonVariant.js";
	import Formatted from "./common/Formatted.svelte";
	import SideModal from "./common/SideModal.svelte";
	import DatePicker from "./form/DatePicker.svelte";

	const { tenant, mothersBlendProduct, cafeinaProduct, subcategories, exchangeRatesSetting } = getGlobalTemplateProps();
	const { MOTHERS_BLEND_CONSTANT_PRICE_ENABLED } = getGlobalSettings();

	let { show = $bindable(false) }: { show?: boolean } = $props();

	export function open(): void {
		show = true;
	}

	const cart = $derived(Cart.getCorrectCartInStepOrThrow(CartStep.PaymentDelivery));
	const id = $props.id();

	const translate = getTranslate(translations);
	const variantChooseId = `subscription-choose-${id}`;
	const selectionChooseId = `subscription-selection-${id}`;
	const sizeChooseId = `subscription-size-${id}`;
	const frequencyChooseId = `subscription-frequency-${id}`;
	const startsAtId = `subscription-starts-at-${id}`;

	const isDesktop = createReactiveBreakpoint(">2xl");

	const weeksInflect = {
		[Locale.cs]: makeInflect("týden", "týdny", "týdnů"),
		[Locale.en]: makeInflect("week", "weeks", "weeks"),
	};

	const today = $derived.by(() => {
		void cart.subscriptionModalStep;

		const now = new Date();
		now.setHours(0, 0, 0, 0);
		return now;
	});

	const selectedWeeks = $derived(subscriptionFrequencyToWeeksMap[cart.subscription.frequency]);
	const shippingDateRange = $derived(calculateMembershipShippingDate(today, cart.subscription.frequency));

	const savings = $derived.by(() => {
		if (Cart.destinationCountry === undefined || Cart.vatType === undefined || Cart.currency === undefined) {
			return undefined;
		}

		const { withTax, percentage } = calculateSubscriptionSavings(
			subcategories,
			Cart.destinationCountry,
			cart.subscription,
			mothersBlendProduct,
			cafeinaProduct,
			Cart.currency,
			Cart.vatType,
			exchangeRatesSetting?.exchangeRates ?? [],
			MOTHERS_BLEND_CONSTANT_PRICE_ENABLED,
		);

		if (withTax <= 0 || percentage <= 0) {
			return undefined;
		}

		return {
			withTax: formatPrice(withTax, Cart.currency, tenant.locale, false),
			percentage: formatPercentage(percentage, tenant.locale, false),
		};
	});

	const nextDispatchDate = $derived.by(() => {
		if (!cart.subscription.startsAt) {
			return undefined;
		}
		const date = new Date(cart.subscription.startsAt);
		date.setDate(date.getDate() + selectedWeeks * 7);
		return date;
	});

	const startsAtMissing = $derived(
		cart.subscriptionModalStep === 1 && Number.isNaN(Date.parse(cart.subscription.startsAt)),
	);
</script>

<SideModal bind:show fullContentWidth heading={translate("heading")}>
	<Stepper max={2} bind:current={cart.subscriptionModalStep} />
	<div class="grow shrink px-4 md:px-8 overflow-y-auto flex flex-col gap-6 sm:gap-4 pb-10 pt-4 md:pt-12 sm:pt-8">
		{#if cart.subscriptionModalStep === 0}
			<div role="radiogroup" class="flex flex-col gap-3 sm:mb-3" aria-labelledby={variantChooseId}>
				<h3 id={variantChooseId} class="flex gap-3.5 items-center">
					<span aria-hidden="true" class="select-none text-[.9375rem]/none tracking-[-0.15px] text-fcrbrown">01</span>
					<span class="text-sm/tighter">
						<Formatted text={translate("variantChoose")} />
					</span>
				</h3>
				<div class="grid grid-cols-2 gap-x-3">
					{#each Object.values(SubscriptionVariant) as variant (variant)}
						<SubcategoryButton
							role="radio"
							class="!w-auto"
							active={cart.subscription.variant === variant}
							aria-checked={cart.subscription.variant === variant}
							onclick={(): void => {
								Cart.saveSubscriptionFields({
									variant,
									...(variant === SubscriptionVariant.Filter && { selection: SubscriptionSelection.FathersChoice }),
								});
							}}
							icon={subscriptionVariantIconMap[variant]}
							label={subscriptionVariantNameMap[variant][tenant.locale]}
						/>
					{/each}
				</div>
			</div>
			<div role="radiogroup" class="flex flex-col gap-3" aria-labelledby={selectionChooseId}>
				<h3 id={selectionChooseId} class="flex gap-3.5 items-center">
					<span aria-hidden="true" class="select-none text-[.9375rem]/none tracking-[-0.15px] text-fcrbrown">02</span>
					<span class="text-sm/tighter">
						<Formatted text={translate("selectionChoose")} />
					</span>
				</h3>
				<div class="grid sm:grid-cols-2 xl:grid-cols-3 gap-2 md:gap-3.5">
					{#each Object.values(SubscriptionSelection) as selection (selection)}
						{#if selection === SubscriptionSelection.FathersChoice}
							<SubscriptionSelectionCard
								title={translate("fathersChoice")}
								subtitle={translate("fathersChoiceTitle")}
								description={translate("fathersChoiceDescription")}
								horizontal={cart.subscription.variant === SubscriptionVariant.Filter}
								active={cart.subscription.selection === selection}
								class={[cart.subscription.variant === SubscriptionVariant.Filter && "col-span-full"]}
								onclick={(): void => {
									Cart.saveSubscriptionFields({ selection });
								}}
							>
								<div class="inset-0 absolute bg-fcrbeige flex items-center justify-center user-select-none">
									<div
										class="rounded-full border-2 border-dashed border-fcrdarkgray/25 flex items-center justify-center h-1/2 aspect-square"
									>
										<span class="text-5xl/none">?</span>
									</div>
								</div>
							</SubscriptionSelectionCard>
						{:else if cart.subscription.variant === SubscriptionVariant.Espresso}
							{@const product = {
								[SubscriptionSelection.MothersBlend]: mothersBlendProduct,
								[SubscriptionSelection.Cafeina]: cafeinaProduct,
							}[selection]}
							{@const mediaSrc = getProductPrimaryMedia(product, [])}
							<SubscriptionSelectionCard
								title={product.name[tenant.locale]}
								subtitle={translate(`title${selection}`)}
								description={translate(`description${selection}`)}
								active={cart.subscription.selection === selection}
								horizontal={false}
								onclick={(): void => {
									Cart.saveSubscriptionFields({ selection });
								}}
							>
								{#if mediaSrc}
									<ProductCardMedia {mediaSrc} alt="" />
								{/if}
							</SubscriptionSelectionCard>
						{/if}
					{/each}
				</div>
			</div>
		{/if}
		{#if cart.subscriptionModalStep === 1}
			<div role="radiogroup" class="flex flex-col gap-3 sm:mb-3" aria-labelledby={sizeChooseId}>
				<h3 id={sizeChooseId} class="flex gap-3.5 items-center">
					<span aria-hidden="true" class="select-none text-[.9375rem]/none tracking-[-0.15px] text-fcrbrown">03</span>
					<span class="text-sm/tighter">
						<Formatted text={translate("sizeChoose")} />
					</span>
				</h3>
				<div class="grid grid-cols-3 md:grid-cols-2 xl:grid-cols-3 gap-1 md:gap-3.5">
					{#each Object.values(SubscriptionSize) as size (size)}
						<Button
							aria-checked={cart.subscription.size === size}
							variant={ButtonVariant.Subcategory}
							size={ButtonSize.Custom}
							class="aria-checked:border-fcrdarkgray border-2 h-12 sm:h-14 lg:h-16 xl:h-18 px-3 sm:px-4 md:px-5 lg:px-6"
							onclick={(): void => {
								Cart.saveSubscriptionFields({ size });
							}}
						>
							<div class="flex flex-col md:flex-row md:gap-1.5 w-full justify-center">
								<span class="flex items-center justify-center gap-1.5 font-sans">
									{#if size === SubscriptionSize.G250x2 && cart.subscription.selection === SubscriptionSelection.FathersChoice}
										<span><Formatted text={translate(`size${size}${cart.subscription.selection}`)} /></span>
									{:else}
										<span><Formatted text={translate(`size${size}`)} /></span>
									{/if}
								</span>
								<span class="text-2xs/none md:text-base font-normal text-fcrgray">
									<Formatted text="({subscriptionSizeNameMap[size][tenant.locale]})" />
								</span>
							</div>
						</Button>
					{/each}
				</div>
			</div>
			<div role="radiogroup" class="flex flex-col gap-3 sm:mb-3" aria-labelledby={frequencyChooseId}>
				<h3 id={frequencyChooseId} class="flex gap-3.5 items-center">
					<span aria-hidden="true" class="select-none text-[.9375rem]/none tracking-[-0.15px] text-fcrbrown">04</span>
					<span class="text-sm/tighter">
						<Formatted text={translate("frequencyChoose")} />
					</span>
				</h3>
				<div class="grid grid-cols-4 sm:grid-cols-5 gap-2">
					{#each Object.values(SubscriptionFrequency) as frequency (frequency)}
						{@const weeks = subscriptionFrequencyToWeeksMap[frequency]}
						<Button
							aria-checked={cart.subscription.frequency === frequency}
							variant={ButtonVariant.Subcategory}
							size={ButtonSize.Custom}
							class={[
								"aria-checked:outline aria-checked:outline-2 aria-checked:outline-fcrdarkgray",
								"h-18 px-3 sm:px-4 md:px-5 lg:px-6 [&:is(button)]:rounded-lg",
							]}
							onclick={(): void => {
								Cart.saveSubscriptionFields({ frequency });
							}}
						>
							<span class="flex items-center flex-col gap-0.5 w-full font-sans">
								<span class="text-[.625rem]/none tracking-[0.025rem] text-fcrgray font-normal">
									<Formatted text={translate("each")} />
								</span>
								<span class="text-1.5xl/6">
									<Formatted text={weeks.toString()} />
								</span>
								<span class="text-[.625rem]/none tracking-[0.025rem] text-fcrgray font-normal">
									<Formatted text={weeksInflect[tenant.locale](weeks)} />
								</span>
							</span>
						</Button>
					{/each}
				</div>
				<EcoFriendlyTip />
			</div>
			<div role="group" class="flex flex-col gap-2" aria-labelledby={startsAtId}>
				<h3 id={startsAtId} class="flex gap-3.5 items-center mb-1">
					<span aria-hidden="true" class="select-none text-[.9375rem]/none tracking-[-0.15px] text-fcrbrown">05</span>
					<span class="text-sm/tighter">
						<Formatted text={translate("startsAt")} />
					</span>
				</h3>
				<DatePicker
					bind:value={cart.subscription.startsAt}
					min={shippingDateRange.nearest}
					max={shippingDateRange.farthest}
					excludedDays={[0, 5, 6]}
				/>
				{#if nextDispatchDate}
					<div class="text-xs/tighter text-fcrgray">
						<Formatted
							text={translate("nextDispatchDate", { date: toDateMonthYearFormat(nextDispatchDate, tenant.locale) })}
						/>
					</div>
				{/if}
			</div>
			<FathersChoiceCancellable />
		{/if}
		{#if Auth.customerType !== CustomerType.B2c}
			<FathersChoiceB2cOnly />
		{/if}
	</div>
	<div
		class={[
			"flex flex-col grow-0 gap-2 xl:gap-3.5 shrink-0",
			"bg-fcrbeige px-4 md:px-12 pb-[max(env(safe-area-inset-bottom,0),0.5rem)] xl:pb-4 2xl:pb-6",
			"sticky md:static w-full z-10 bottom-0",
		]}
	>
		<div class="flex flex-col xl:gap-4 gap-1 text-fcrdarkgray font-medium pt-4 md:pt-4 2xl:pt-6">
			<div class="flex flex-col gap-1">
				<div class="text-xs/none md:text-sm/none pb-px">
					<Formatted text={translate("monthly")} />
				</div>
				<div class="text-3xl/none md:text-4xl/none">
					{#if Cart.currency !== undefined}
						<Formatted text={formatPrice(Cart.subscriptionPrice.withTax, Cart.currency, tenant.locale, false)} />
					{/if}
				</div>
			</div>
			{#if savings}
				<div class="text-2xs/none md:text-xs/tighter text-fcrgray mt-1 font-normal">
					<Formatted text={translate("subscriptionSavings", { savings: savings.withTax })} />
				</div>
			{/if}
		</div>
		<ul class="gap-x-1.5 gap-y-1 md:flex-wrap hidden md:flex">
			{#if savings}
				<SubscriptionDiscountChip icon={leaf} label={translate("discount", { discount: savings.percentage })} />
			{/if}
			<SubscriptionDiscountChip icon={box} label={translate("freeShipping")} />
			<SubscriptionDiscountChip icon={shield} label={translate("membership")} />
		</ul>
		<Button
			size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
			variant={ButtonVariant.Dark}
			disabled={Auth.customerType !== CustomerType.B2c || startsAtMissing}
			onclick={(): void => {
				if (cart.subscriptionModalStep === 0) {
					cart.subscriptionModalStep = 1;
				} else if (Cart.addSubscription()) {
					location.assign(getCartPath(tenant));
				}
			}}
			class="w-full"
		>
			{#if cart.subscriptionModalStep === 0}
				<Formatted text={translate("continue")} />
			{:else if cart.hasSubscription}
				<Formatted text={translate("continueToCheckout")} />
			{:else}
				<Formatted text={translate("order")} />
			{/if}
		</Button>
	</div>
</SideModal>
